import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Shield,
  Settings,
  Users,
  CreditCard,
  Palette,
  LogOut,
  LayoutDashboard,
  ExternalLink,
  FileText,
  DollarSign,
  AlertCircle,
  Megaphone,
  PanelLeftClose,
  PanelLeft,
  Pin,
  PinOff,
  Bot,
  BarChart3,
} from "lucide-react";

const navItems = [
  { name: "Settings", section: "settings", icon: Settings },
  { name: "Analytics", section: "analytics", icon: BarChart3 },
  { name: "CMS", section: "cms", icon: FileText },
  { name: "Branding", section: "branding", icon: Palette },
  { name: "Stripe", section: "stripe", icon: CreditCard },
  { name: "Payments", section: "payments", icon: DollarSign },
  { name: "Users", section: "users", icon: Users },
  { name: "Renewals", section: "renewals", icon: AlertCircle },
  { name: "Bot", section: "bot", icon: Bot },
  { name: "Promotions", section: "promotions", icon: Megaphone },
  { name: "Security", section: "security", icon: Shield },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [adminUser, setAdminUser] = useState(null);
  const activeSection = searchParams.get("section") || "settings";
  
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("admin_sidebar_collapsed");
    return saved ? JSON.parse(saved) : false;
  });
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("admin_sidebar_pinned");
    return saved ? JSON.parse(saved) : true;
  });
  const [sidebarHovered, setSidebarHovered] = useState(false);

  // Determine if sidebar should be expanded
  const sidebarExpanded = !sidebarCollapsed || (sidebarCollapsed && !sidebarPinned && sidebarHovered);

  useEffect(() => {
    const validateAdminSession = async () => {
      const token = localStorage.getItem("admin_token");
      const user = localStorage.getItem("admin_user");
      
      if (!token) {
        // No session - redirect to home page
        navigate("/");
        return;
      }
      
      // Validate the admin token is still valid
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
        const response = await fetch(`${API_URL}/admin/validate`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          // Token is invalid, clear and redirect to home
          localStorage.removeItem("admin_token");
          localStorage.removeItem("admin_user");
          navigate("/");
          return;
        }
        
        if (user) {
          setAdminUser(JSON.parse(user));
        }
      } catch (error) {
        // On error, clear session and redirect to home
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        navigate("/");
      }
    };
    
    validateAdminSession();
  }, [navigate]);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem("admin_sidebar_collapsed", JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem("admin_sidebar_pinned", JSON.stringify(sidebarPinned));
  }, [sidebarPinned]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const handleSectionChange = (section) => {
    setSearchParams({ section });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const togglePin = () => {
    setSidebarPinned(!sidebarPinned);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="h-screen flex overflow-hidden bg-[#09090b]">
        {/* Sidebar */}
        <aside 
          className={`bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out ${
            sidebarExpanded ? "w-64" : "w-16"
          } ${!sidebarPinned && sidebarCollapsed ? "absolute z-50 h-full shadow-xl" : "relative"}`}
          onMouseEnter={() => setSidebarHovered(true)}
          onMouseLeave={() => setSidebarHovered(false)}
        >
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <button 
              onClick={() => navigate('/')} 
              className="flex items-center min-w-0 hover:opacity-80 transition-opacity"
            >
              <Shield className="w-6 h-6 text-primary flex-shrink-0" />
              {sidebarExpanded && (
                <span className="font-bold text-lg tracking-tight ml-2 whitespace-nowrap">Admin Panel</span>
              )}
            </button>
            {sidebarExpanded && (
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={togglePin}
                    >
                      {sidebarPinned ? (
                        <Pin className="w-4 h-4 text-primary" />
                      ) : (
                        <PinOff className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {sidebarPinned ? "Unpin sidebar (auto-hide)" : "Pin sidebar"}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={toggleSidebar}
                    >
                      <PanelLeftClose className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Collapse sidebar
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>

          {/* Expand button when collapsed */}
          {!sidebarExpanded && (
            <div className="p-2 border-b border-border">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-full h-10"
                    onClick={toggleSidebar}
                  >
                    <PanelLeft className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Expand sidebar
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className={`space-y-1 ${sidebarExpanded ? "px-3" : "px-2"}`}>
              {sidebarExpanded && (
                <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Dashboard
                </p>
              )}
              {navItems.map((item) => (
                <Tooltip key={item.section}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleSectionChange(item.section)}
                      className={`w-full flex items-center gap-3 rounded-lg text-sm transition-colors ${
                        sidebarExpanded ? "px-3 py-2.5" : "px-3 py-3 justify-center"
                      } ${
                        activeSection === item.section
                          ? "bg-primary/10 text-primary border border-primary/20"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      {sidebarExpanded && <span>{item.name}</span>}
                    </button>
                  </TooltipTrigger>
                  {!sidebarExpanded && (
                    <TooltipContent side="right">
                      {item.name}
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className={`border-t border-border ${sidebarExpanded ? "p-4 space-y-3" : "p-2 space-y-2"}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className={`gap-2 text-muted-foreground ${sidebarExpanded ? "w-full" : "w-full justify-center"}`}
                  onClick={() => window.open('/', '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                  {sidebarExpanded && "View Site"}
                </Button>
              </TooltipTrigger>
              {!sidebarExpanded && (
                <TooltipContent side="right">View Site</TooltipContent>
              )}
            </Tooltip>
            
            {sidebarExpanded && (
              <div className="px-3 py-2">
                <p className="text-sm font-medium truncate">{adminUser?.name || "Admin"}</p>
                <p className="text-xs text-muted-foreground truncate">{adminUser?.email}</p>
              </div>
            )}
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className={`gap-2 text-muted-foreground hover:text-foreground ${sidebarExpanded ? "w-full" : "w-full justify-center"}`}
                  onClick={handleLogout}
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  {sidebarExpanded && "Logout"}
                </Button>
              </TooltipTrigger>
              {!sidebarExpanded && (
                <TooltipContent side="right">Logout</TooltipContent>
              )}
            </Tooltip>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <Outlet context={{ activeSection, setActiveSection: handleSectionChange }} />
        </main>
      </div>
    </TooltipProvider>
  );
};

export default AdminLayout;
